import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useTeamSearch } from '@/hooks/useTeamSearch';
import { supabase } from '@/integrations/supabase/client';
import { GameWithDetails } from '@/types/gameSystem';

interface TeamAssignmentFormProps {
  game: GameWithDetails;
  onAssignTeam: (teamSide: 'home' | 'away', teamId: number, teamName: string) => void;
  currentUserId?: string;
}

const normalizeSportName = (value?: string | null) =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

export const TeamAssignmentForm = ({ 
  game, 
  onAssignTeam, 
  currentUserId 
}: TeamAssignmentFormProps) => {
  const [homeTeamSelection, setHomeTeamSelection] = useState<string>('');
  const [awayTeamSelection, setAwayTeamSelection] = useState<string>('');
  const [resolvedSportName, setResolvedSportName] = useState(game.sport?.sport_name || '');
  const [sportLoading, setSportLoading] = useState(Boolean(game.sport_id && !game.sport?.sport_name));
  const { teams, loading } = useTeamSearch({ 
    sportFilter: resolvedSportName 
  });

  useEffect(() => {
    let cancelled = false;

    const loadSportName = async () => {
      const directSportName = game.sport?.sport_name?.trim();

      if (directSportName) {
        setResolvedSportName(directSportName);
        setSportLoading(false);
        return;
      }

      if (!game.sport_id) {
        setResolvedSportName('');
        setSportLoading(false);
        return;
      }

      setSportLoading(true);

      const { data, error } = await supabase
        .from('available_sports')
        .select('sport_name')
        .eq('id', game.sport_id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error('Error loading sport name for team assignment:', error);
        setResolvedSportName('');
        setSportLoading(false);
        return;
      }

      setResolvedSportName(data?.sport_name || '');
      setSportLoading(false);
    };

    loadSportName();

    return () => {
      cancelled = true;
    };
  }, [game.sport?.sport_name, game.sport_id]);

  const sportLabel = resolvedSportName || game.sport?.sport_name || '';
  const visibleTeams = useMemo(() => {
    const normalizedSport = normalizeSportName(sportLabel);

    if (!normalizedSport) return [];

    return teams.filter((team) => normalizeSportName(team.sport_type) === normalizedSport);
  }, [sportLabel, teams]);

  const isLoadingTeams = loading || sportLoading;

  const canAddHomeTeam = !game.home_team_id && currentUserId && ['pending', 'approved', 'confirmed'].includes(game.game_status || 'pending');
  const canAddAwayTeam = !game.away_team_id && currentUserId && ['pending', 'approved', 'confirmed'].includes(game.game_status || 'pending');

  const handleHomeTeamSubmit = () => {
    if (!homeTeamSelection) return;
    
    if (homeTeamSelection === 'tbd') {
      // Pass a special value for TBD that the backend can recognize
      onAssignTeam('home', null as any, 'TBD (To Be Determined)');
    } else {
      const selectedTeam = visibleTeams.find(t => t.team_id.toString() === homeTeamSelection);
      if (selectedTeam) {
        onAssignTeam('home', selectedTeam.team_id, selectedTeam.team_name);
      }
    }
    setHomeTeamSelection('');
  };

  const handleAwayTeamSubmit = () => {
    if (!awayTeamSelection) return;
    
    if (awayTeamSelection === 'tbd') {
      // Pass a special value for TBD that the backend can recognize
      onAssignTeam('away', null as any, 'TBD (To Be Determined)');
    } else {
      const selectedTeam = visibleTeams.find(t => t.team_id.toString() === awayTeamSelection);
      if (selectedTeam) {
        onAssignTeam('away', selectedTeam.team_id, selectedTeam.team_name);
      }
    }
    setAwayTeamSelection('');
  };

  if (!canAddHomeTeam && !canAddAwayTeam) {
    return null;
  }

  return (
    <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
      <div className="flex flex-col space-y-2">
        <h4 className="font-medium text-sm text-muted-foreground">Assign Teams to Game</h4>
        {sportLabel && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Sport:</span>
            <span className="font-medium text-primary">{sportLabel}</span>
            <span className="text-muted-foreground">• Only {sportLabel} teams can be assigned</span>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Add Home Team */}
        {canAddHomeTeam && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Add Home Team</Label>
            <Select value={homeTeamSelection} onValueChange={setHomeTeamSelection}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select home team..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border z-[9999] max-h-60 text-white">
                <SelectItem value="tbd">
                  <div className="flex flex-col">
                    <span className="font-medium">TBD (To Be Determined)</span>
                    <span className="text-xs text-muted-foreground">Team will be assigned later</span>
                  </div>
                </SelectItem>
                {isLoadingTeams ? (
                  <SelectItem value="loading" disabled>Loading teams...</SelectItem>
                ) : visibleTeams.length === 0 ? (
                  <SelectItem value="no-teams" disabled>
                    No {sportLabel || 'matching'} teams available
                  </SelectItem>
                ) : (
                  visibleTeams
                    .filter(team => team.team_id !== game.away_team_id) // Don't show away team in home options
                    .map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id.toString()}>
                        <div className="flex flex-col">
                          <span className="font-medium">{team.team_name}</span>
                          <span className="text-xs text-muted-foreground">{team.sport_type}</span>
                        </div>
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleHomeTeamSubmit}
              disabled={!homeTeamSelection || isLoadingTeams}
              size="sm"
              className="w-full"
            >
              Assign Home Team
            </Button>
          </div>
        )}

        {/* Add Away Team */}
        {canAddAwayTeam && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Add Away Team</Label>
            <Select value={awayTeamSelection} onValueChange={setAwayTeamSelection}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select away team..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border z-[9999] max-h-60 text-white">
                <SelectItem value="tbd">
                  <div className="flex flex-col">
                    <span className="font-medium">TBD (To Be Determined)</span>
                    <span className="text-xs text-muted-foreground">Team will be assigned later</span>
                  </div>
                </SelectItem>
                {isLoadingTeams ? (
                  <SelectItem value="loading" disabled>Loading teams...</SelectItem>
                ) : visibleTeams.length === 0 ? (
                  <SelectItem value="no-teams" disabled>
                    No {sportLabel || 'matching'} teams available
                  </SelectItem>
                ) : (
                  visibleTeams
                    .filter(team => team.team_id !== game.home_team_id) // Don't show home team in away options
                    .map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id.toString()}>
                        <div className="flex flex-col">
                          <span className="font-medium">{team.team_name}</span>
                          <span className="text-xs text-muted-foreground">{team.sport_type}</span>
                        </div>
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleAwayTeamSubmit}
              disabled={!awayTeamSelection || isLoadingTeams}
              size="sm"
              className="w-full"
            >
              Assign Away Team
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};